import React, { useState, useEffect } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Input } from "../design/input";
import { Field, Fieldset, FieldGroup, Label } from "../design/fieldset";
import { Textarea } from "../design/textarea";
import { useFile } from "../contexts/FileContext";
import { PlusIcon } from "@heroicons/react/20/solid";

interface Props {
  folderId?: string;
  className?: string;
  children?: React.ReactNode;
}

const CreateFileButton: React.FC<Props> = ({
  className,
  children,
  folderId,
}) => {
  const { createFile } = useFile();
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (file && !name) {
      setName(file.name);
    }
  }, [file, name]);

  const handleSubmit = async () => {
    if (!file) {
      return;
    }

    setIsUploading(true);

    try {
      await createFile(file, {
        type: "evidence", // Assuming the type of file being uploaded is evidence, adjust as needed
        name,
        size: file.size,
        mimeType: file.type,
        description,
        status: "uploading",
        folderId,
      });
      clearForm();
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Failed to upload file. Please try again later.");
    }

    setIsUploading(false);
  };

  const clearForm = () => {
    setFile(null);
    setName("");
    setDescription("");
    setIsOpen(false);
  };

  return (
    <>
      <Button
        className={className}
        type="button"
        onClick={() => setIsOpen(true)}
      >
        {children ?? (
          <div className="flex items-center gap-2">
            <PlusIcon className="w-4 h-4" />
            <span className="hidden sm:block">Create File</span>
          </div>
        )}
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Create File</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>File</Label>
                <Input
                  type="file"
                  onChange={(e) =>
                    setFile(e.target.files ? e.target.files[0] : null)
                  }
                />
              </Field>
              <Field>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                />
              </Field>
              <Field>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description"
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={!file || isUploading} onClick={handleSubmit}>
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateFileButton;
